import { IconButton, Tooltip, makeStyles } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Queue } from "@material-ui/icons";
import React, { FC, useContext, useState } from "react";
import { useGetExpertsQuery } from "../../api/rtk/ExpertApi";
import { ExamDayType } from "../../model/ExamDayType";
import { TimeSlot, TimeSlotType } from "../../model/TimeSlotStructure";
import { getTimeSlotLabel } from "../../utils/TimeSlotUtils";
import CustomSwitch from "../common/CustomSwitch";
import TranslatedMessage from "../translations/TranslatedMessage";
import { SchedulerContextValue } from "./context/SchedulerContext";
import SchedulerExamSelectDialog from "./SchedulerExamSelectDialog";
import SchedulerInfoRow from "./SchedulerInfoRow";
import SchedulerLanguagesRow from "./SchedulerLanguagesRow";
import SchedulerProctorRow from "./SchedulerProctorRow";
import SchedulerTimeSlotRow from "./SchedulerTimeSlotRow";

const secondaryColor = "#e0e0e0";
const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    maxHeight: `calc(100% - ${theme.spacing(1)}px)`,
    overflow: "auto",
    margin: "1px",
  },
  table: {
    tableLayout: "fixed",
    "& tbody tr td:first-child": {
      position: "sticky",
      left: 0,
      zIndex: 1,
      background: "white",
    },
    "& tbody tr$secondaryRow td:first-child": {
      backgroundColor: secondaryColor,
    },
  },
  headerCell: {
    width: "18rem",
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: secondaryColor,
  },
  expertsTableBody: {
    border: "solid",
    borderColor: secondaryColor,
    borderWidth: "10px",
    borderRight: "none",
    borderLeft: "none",
    borderTop: "none",
    padding: 0,
    margin: 0,
    boxSizing: "border-box",
  },
  roomColumn: {
    width: "10rem",
    left: 0,
    zIndex: 2,
  },
  secondaryRow: {
    "& td": {
      backgroundColor: secondaryColor,
    },
  },
  separationRow: {
    padding: "1px",
  },
  assignExamButton: {
    marginLeft: "8px",
  },
  expertsSwitch: {
    backgroundColor: `${secondaryColor}!important`,
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

const SchedulerTable: FC = () => {
  const classes = useStyles();
  const [examSelectDialogRoomId, setExamSelectDialogRoomId] = useState("");
  const {
    readonly,
    isSessionPublished,
    exams,
    rooms,
    selectedExamDayId,
    examDays,
    timeSlotStructure,
    setAllTimeSlotsExams,
  } = useContext(SchedulerContextValue);
  const selectedExamDay = examDays.find((examDay) => examDay.id === selectedExamDayId);
  const timeSlots = selectedExamDay === undefined ? [] : timeSlotStructure[selectedExamDay.timeSlotStructureType];
  const { data: allExperts } = useGetExpertsQuery();
  const [showAllExperts, setShowAllExperts] = useState(false);

  const closeExamSelectDialog = () => setExamSelectDialogRoomId("");

  const applySelectedExams = (examIds: string[]) => {
    setAllTimeSlotsExams(examSelectDialogRoomId, examIds);
    closeExamSelectDialog();
  };

  const openExamSelectDialog = (roomId: string) => setExamSelectDialogRoomId(roomId);

  const getRoomAssignment = (roomId: string) =>
    selectedExamDay?.roomAssignments.find((roomAssignment) => roomAssignment.roomId === roomId);

  const getExamSelectDialogButton = (roomId: string) => {
    if (readonly || isSessionPublished) {
      return <></>;
    }

    var roomAssignment = getRoomAssignment(roomId);
    if (roomAssignment?.isPreparationRoom) {
      return <></>;
    }

    return (
      <Tooltip title={<TranslatedMessage id="scheduler.examSelectDialog.tooltip" />}>
        <IconButton color="primary" className={classes.assignExamButton} onClick={() => openExamSelectDialog(roomId)}>
          <Queue />
        </IconButton>
      </Tooltip>
    );
  };

  const getRow = (timeSlot: TimeSlot) => {
    const label = getTimeSlotLabel(timeSlot);
    if (timeSlot.type === TimeSlotType.Briefing) {
      return (
        <SchedulerInfoRow
          key={label}
          cellsCount={rooms.length}
          cellContent={<TranslatedMessage id="scheduler.message.briefing" />}
          rowLabel={label}
          className={classes.secondaryRow}
        />
      );
    }

    if (timeSlot.type === TimeSlotType.Break) {
      return (
        <SchedulerInfoRow
          key={label}
          cellsCount={rooms.length}
          cellContent={<TranslatedMessage id="scheduler.message.pause" />}
          rowLabel={label}
          className={classes.secondaryRow}
        />
      );
    }

    return <SchedulerTimeSlotRow key={label} timeSlot={timeSlot} />;
  };

  return (
    <>
      <Paper className={classes.paper}>
        <Table size="medium" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.roomColumn} ${classes.headerCell}`}>
                <TranslatedMessage id="room.header" />
              </TableCell>
              {rooms.map((room) => (
                <TableCell key={room.id} className={classes.headerCell}>
                  <span>{room.name}</span>
                  {getExamSelectDialogButton(room.id)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            className={
              (timeSlots &&
                timeSlots.length > 0 &&
                timeSlots[0].type === TimeSlotType.Exam &&
                classes.expertsTableBody) ||
              undefined
            }
          >
            <SchedulerLanguagesRow />
            {!readonly && (
              <TableRow>
                <>
                  <TableCell className={classes.expertsSwitch}>
                    <CustomSwitch
                      label={<TranslatedMessage id="userRoomAssignment.label.allExpertsSwitch" />}
                      value={showAllExperts}
                      onChange={() => setShowAllExperts((prevState) => !prevState)}
                    />
                  </TableCell>
                  {selectedExamDay?.roomAssignments?.map((x) => (
                    <TableCell className={classes.expertsSwitch} key={x.id} />
                  ))}
                </>
              </TableRow>
            )}
            <SchedulerProctorRow
              isFixed={true}
              allExperts={allExperts}
              showAllExperts={showAllExperts}
              isSessionPublished={isSessionPublished}
              readOnly={readonly}
            />
            <SchedulerProctorRow
              isFixed={false}
              allExperts={allExperts}
              showAllExperts={showAllExperts}
              isSessionPublished={isSessionPublished}
              readOnly={readonly}
            />
          </TableBody>
          <TableBody>{timeSlots?.map(getRow)}</TableBody>
        </Table>
      </Paper>
      <SchedulerExamSelectDialog
        opened={examSelectDialogRoomId !== ""}
        isMulti={selectedExamDay?.type === ExamDayType.Correction}
        exams={exams}
        accept={applySelectedExams}
        decline={closeExamSelectDialog}
      />
    </>
  );
};

export default SchedulerTable;
